import { Link, useParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { ErrorBox, Skeleton, useAsync } from '../../components/Ui.jsx'
import { useRequestSeed } from '../../components/PublicLayout.jsx'
import { diffDays, fmtDate, inr, lakh, todayISO } from '../../lib/format.js'
import { monogram } from '../../lib/monogram.js'

function readyState (readyDate, today) {
  const days = diffDays(today, readyDate)
  if (days <= 0) return { tone: 'now', label: 'Ready now' }
  if (days === 1) return { tone: 'soon', label: 'Ready tomorrow' }
  if (days <= 3) return { tone: 'soon', label: `Ready in ${days} days` }
  return { tone: '', label: `Ready in ${days} days` }
}

export default function HatcheryPage () {
  const { id } = useParams()
  const { data, error, loading, reload } = useAsync(() => api.get(`/public/hatcheries/${id}`), [id])
  const openRequest = useRequestSeed()

  if (loading) {
    return (
      <div className="pub-wrap" style={{ paddingTop: 20 }}>
        <div className="f-card"><Skeleton rows={6} /></div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="pub-wrap" style={{ paddingTop: 24 }}>
        <ErrorBox error={error} onRetry={reload} />
        <Link className="f-btn ghost" to="/hatcheries">← All hatcheries</Link>
      </div>
    )
  }

  const { hatchery: h, batches, showPrices, sampleAnimals, passThreshold, sampleLeadDays, dateWindowDays } = data
  const today = todayISO()
  const mark = monogram(h.name, h.code || h.name)
  const available = batches.reduce((s, b) => s + b.availableLakh, 0)

  return (
    <>
      <section className="f-detail-hero">
        <div className="pub-wrap">
          <Link className="f-back" to="/hatcheries">← All hatcheries</Link>

          <div className="f-detail-id">
            <span className="f-mono lg" style={{ background: mark.bg, color: mark.fg }}>{mark.text}</span>
            <div>
              <h1>{h.name}</h1>
              <div className="f-place">📍 {h.place}</div>
              <div className="f-marks">
                {batches.length > 0
                  ? <span className="f-mark good">✓ {batches.length} batch{batches.length > 1 ? 'es' : ''} upcoming</span>
                  : <span className="f-mark quiet">No batch open right now</span>}
                {h.caaRegistrationNo && <span className="f-mark">✓ CAA registered</span>}
                {h.annualCapacityCr && <span className="f-mark quiet">{h.annualCapacityCr} crore PL / year</span>}
              </div>
            </div>
          </div>

          <div className="f-detail-stats">
            <div><b>{lakh(available)}</b><span>seed available</span></div>
            <div><b>{batches[0] ? fmtDate(batches[0].readyDate) : '—'}</b><span>next ready</span></div>
            <div><b>{[...new Set(batches.map(b => b.plStage))].join(', ') || '—'}</b><span>PL stages</span></div>
          </div>

          <button className="f-btn accent block only-desk" onClick={() => openRequest({ hatchery: h })}>
            Request seed from this hatchery
          </button>
        </div>
      </section>

      <div className="pub-wrap">
        <div className="f-detail-grid">
          <div>
            <h2 className="f-sec">Batches you can ask for</h2>

            {batches.length === 0 && (
              <div className="f-empty">
                <b>No batch is open for booking here right now</b>
                <p>Tell the desk when you need seed and we will call you as soon as this hatchery — or the nearest one with the same stage — has a cycle ready.</p>
                <div className="f-empty-actions">
                  <button className="f-btn accent" onClick={() => openRequest({ hatchery: h })}>Ask the desk</button>
                </div>
              </div>
            )}

            {batches.map(b => {
              const ready = readyState(b.readyDate, today)
              return (
                <article className={`f-card f-batch ${ready.tone === 'now' ? 'is-ready' : ''}`} key={b.id}>
                  <div className="f-batch-top">
                    <div>
                      <div className="f-batch-stage">{b.plStage}</div>
                      <div className="f-batch-species">{b.species} · {b.broodstockSource}</div>
                    </div>
                    <div className="f-tagrow right">
                      <span className={`f-tag ${ready.tone}`}>{ready.label}</span>
                      <span className="f-tag-date">{fmtDate(b.readyDate)}</span>
                    </div>
                  </div>

                  <div className="f-facts four">
                    <div><b>{lakh(b.availableLakh)}</b><span>available</span></div>
                    <div><b>{lakh(b.minOrderLakh)}</b><span>minimum</span></div>
                    <div><b>{b.salinityPpt ?? '—'} ppt</b><span>salinity</span></div>
                    <div><b>{b.stressTestPct ?? '—'}%</b><span>stress test</span></div>
                    {showPrices && <div><b>{inr(b.basePricePer1000)}</b><span>per 1,000</span></div>}
                    <div><b>CV {b.sizeCvPct ?? '—'}%</b><span>uniformity</span></div>
                  </div>

                  {showPrices && b.offer && <div className="f-offer">🎁 {b.offer.details}</div>}
                  {b.feedProtocol && <div className="f-note">🍤 {b.feedProtocol}</div>}

                  <div className="f-pcr">
                    <div className="f-marks">
                      {[['WSSV', b.pcr.wssv], ['EHP', b.pcr.ehp], ['IHHNV', b.pcr.ihhnv]].map(([label, val]) => (
                        <span key={label} className={`f-mark ${val === 'negative' ? 'good' : val ? 'bad' : 'quiet'}`}>
                          {val === 'negative' ? '✓' : val ? '!' : '–'} {label} {val || 'not tested'}
                        </span>
                      ))}
                    </div>
                    {b.pcr.labName && (
                      <div className="f-pcr-lab">
                        Screened at {b.pcr.labName}{b.pcr.screenDate ? ` on ${fmtDate(b.pcr.screenDate)}` : ''}
                      </div>
                    )}
                  </div>

                  <div className="f-card-actions">
                    {b.pcr.reportPath && (
                      <a className="f-btn ghost" href={b.pcr.reportPath} target="_blank" rel="noreferrer">PCR certificate</a>
                    )}
                    <button className="f-btn accent" onClick={() => openRequest({ hatchery: h, batch: b })}>
                      Request this batch
                    </button>
                  </div>
                </article>
              )
            })}
          </div>

          <aside>
            <div className="f-card f-side">
              <h3>How to book this seed</h3>
              <ol className="pub-steps">
                <li>
                  <b>Ask for a call back</b>
                  Tell us the quantity and the date your pond will be ready. Book 7–10 days ahead where you can.
                </li>
                <li>
                  <b>We block the seed</b>
                  Pay the advance and the lot is held. Your stocking date can slip by up to {dateWindowDays} days, never earlier.
                </li>
                <li>
                  <b>Test the sample first</b>
                  {sampleAnimals} animals reach you {sampleLeadDays} days before stocking. Below {passThreshold}% survival you do not take the lot — we find you another hatchery for the same date.
                </li>
                <li>
                  <b>We deliver to your pond</b>
                  Escorted transport with the certificates, handed over against an OTP.
                </li>
              </ol>
            </div>

            {h.biosecurityNotes && (
              <div className="f-card f-side">
                <h3>Biosecurity here</h3>
                <p className="f-side-body">{h.biosecurityNotes}</p>
              </div>
            )}

            <div className="f-card f-side">
              <h3>Hatchery record</h3>
              <dl className="f-record">
                <div><dt>Code</dt><dd className="mono">{h.code}</dd></div>
                <div><dt>CAA registration</dt><dd className="mono">{h.caaRegistrationNo || '—'}</dd></div>
                <div><dt>Village</dt><dd>{h.village || '—'}</dd></div>
                <div><dt>Mandal</dt><dd>{h.mandal || '—'}</dd></div>
                <div><dt>District</dt><dd>{h.district || '—'}</dd></div>
                <div><dt>Broodstock</dt><dd>{h.broodstockPartners || '—'}</dd></div>
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </>
  )
}
